<template>
  <div class="uploader-container">
    <div class="pending-container" v-if="showPending">
      <van-icon class="pending-icon" name="warning-o" />
      <div class="pending-text">{{ field.pendingText || $t(`${i18nPrefix}.message.pendingUpload`) }}</div>
    </div>
    <div class="warn-container" v-if="warnMessage">
      <van-icon class="warn-icon" name="warning-o" />
      <van-cell class="warn-message" :value="warnMessage" />
    </div>
    <div class="error-container" v-if="errorMessage">
      <van-icon class="error-icon" name="warning-o" />
      <van-cell class="error-message" :value="errorMessage" />
    </div>
    <van-uploader
      v-model="fileList"
      :accept="accept"
      :before-read="beforeRead"
      :after-read="afterRead"
      :deletable="false"
      :disabled="field.maxSize ? fileList.length >= field.maxSize : false"
      :preview-full-image="field.preview"
      :capture="field.capture"
      @click-preview="preview"
    >
      <van-button class="upload-button" :disabled="field.maxSize ? fileList.length >= field.maxSize : false">{{ field.btnName }}</van-button>
      <template #preview-cover="{ file, index }">
        <div class="preview-cover">
          <div class="file-info">
            <van-icon class="iconfont icona-basicinformation preview-icon"></van-icon>
            <div class="preview-name">{{ file.name }}</div>
            <van-icon class="iconfont iconDelete delete-icon" @click="handleDelete(file, index)"></van-icon>
          </div>
          <template>
            <van-loading v-if="uploadLoading" class="upload-loading"/>
            <van-icon v-else class="upload-status" :class="uploadStatusClass" :name="uploadIcon"></van-icon>
          </template>
        </div>
      </template>
    </van-uploader>
    <van-cell v-for="(tip, index) in field.tips" class="upload-cell" :title="tip" :key="index">
    </van-cell>
    <van-field
      v-if="!field.noDescription"
      class="field"
      clearable
      v-model="data[field.name].description"
      :placeholder="field.placeholder"
    />
    <van-image-preview v-model="showPreview" closeable>
      <template #index></template>
      <template #cover>
        <div class="video-preview">
          <video
            playsinline
            webkit-playsinline
            x-webkit-airplay
            ref="video"
            :src="videoUrl"
            controls="controls"
            preload="auto"
            class="video"
            >
          </video>
          <div v-show="showPlayIcon" class="play-icon icomoon-triangle" @click="handelPlayVideo"></div>
        </div>
      </template>
    </van-image-preview>
  </div>
</template>

<script>
import _ from 'lodash'
import { uploadFile, getFileUID } from '@/utils/qiniu'
import { mapActions } from 'vuex'
import { QUESTION_TYPE as OPEN_QUESTION_TYPE } from '@/constant/openQuestion'
import { deviceType } from '@/utils/common'

export default {
  components: {
  },
  props: {
    field: {
      required: true
    },
    data: {}
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      showPlayIcon: false,
      showPreview: false,
      videoUrl: '',
      videoKey: '',
      fileList: [],
      uploadLoading: true,
      uploadStatus: true,
      errorMessage: '',
      warnMessage: ''
    }
  },
  computed: {
    showPending () {
      return this.fileList.length <= 0 || this.uploadLoading
    },
    accept () {
      if (this.field.type === OPEN_QUESTION_TYPE.VIDEO) {
        return this.field?.fileType?.toString() || 'video/*'
      } else if (this.field.type === OPEN_QUESTION_TYPE.ATTACHMENT || deviceType.isAndroid) {
        return '*/*'
      } else if (!deviceType.isiOS) {
        return 'application/*'
      } else {
        return this.field.fileType?.toString() || ''
      }
    },
    uploadIcon () {
      if (this.uploadStatus) {
        return 'passed'
      } else {
        return 'close'
      }
    },
    uploadStatusClass () {
      if (this.uploadStatus) {
        return 'success'
      } else {
        return 'error'
      }
    }
  },
  mounted () {

  },
  methods: {
    ...mapActions('personal', ['getJobQuestionVideoPaly', 'updateJobQuestionVideo']),
    ...mapActions('application', ['handleOpenQuestionUploadedVideo', 'getOpenQuestionUploadedVideoDuration']),
    beforeRead (file) {
      if (!this.field.byCamera && this.field.fileType && !this.field.fileType.includes(file.type)) {
        this.errorMessage = this.$t(`${this.i18nPrefix}.message.wrongFileType`)
        return false
      } else if (this.field.uploaderFileSize && file.size > this.field.uploaderFileSize) {
        this.errorMessage = this.$t(`${this.i18nPrefix}.message.wrongFileSize`)
        this.field.fileSizeError?.()
        return false
      }
      this.errorMessage = ''
      return true
    },
    async afterRead (fileInfo) {
      let isCustomUpload = false
      this.$emit('uploadFile', fileInfo, (success) => {
        if (success) { // 自定义上传成功
          this.uploadStatus = true
        } else { // 自定义上传失败
          this.uploadStatus = false
        }
        this.uploadLoading = false
        isCustomUpload = true
      })
      // 不使用自定义文件上传
      if (!isCustomUpload) {
        const videoKey = await this.uploadFileToQiNiu(fileInfo)
        this.data[this.field.name].videoKey = videoKey
        this.$emit('uploadQiNiuKey', videoKey)
        this.videoKey = videoKey
        this.updateVideoKey(1)
      }
    },
    async preview (fileInfo) {
      if (this.uploadLoading) {
        return
      }
      if (this.field.type === OPEN_QUESTION_TYPE.VIDEO) {
        this.showPreview = true
        const resp = await this.getJobQuestionVideoPaly({ video_key: this.videoKey })
        this.showPlayIcon = true
        this.videoUrl = resp.private_mp4_url
        this.$nextTick(() => {
          this.$refs.video.addEventListener('play', () => {
            this.showPlayIcon = false
          })
          this.$refs.video.addEventListener('pause', () => {
            this.showPlayIcon = true
          })
        })
      }
    },
    async uploadFileToQiNiu (fileInfo) {
      const file = fileInfo.file
      const fileName = file.name
      const ext = fileName.slice(fileName.indexOf('.'))
      const fileKey = `${getFileUID()}${ext}`
      this.uploadLoading = true
      try {
        let resp = await uploadFile(file, fileKey, false)
        if (this.field.type === OPEN_QUESTION_TYPE.VIDEO) {
          resp = await this.handleOpenQuestionUploadedVideo({
            file_key: fileKey
          })
          const persistentId = resp.persistent_id
          const maxDuration = parseFloat(this.field.duration)
          if (maxDuration) {
            let duration = resp.duration
            let isFinished = resp.is_finished
            while (!isFinished) {
              resp = await this.getOpenQuestionUploadedVideoDuration({ persistent_id: persistentId })
              duration = resp.duration
              isFinished = resp.is_finished
              await new Promise((resolve, reject) => {
                setTimeout(resolve, 1000)
              })
            }
            if (duration > maxDuration) {
              this.warnMessage = this.$t(`${this.i18nPrefix}.message.questionVideoIsTooLong`, { duration: this.field.maxDuration })
            } else {
              this.warnMessage = ''
            }
          }
        }
        this.uploadStatus = true
        return fileKey
      } catch (e) {
        this.uploadStatus = false
        this.fileList.splice(0)
      } finally {
        this.uploadLoading = false
      }
    },
    async updateVideoKey (isDelete) {
      const params = {
        question_id: this.field.id,
        video_key: this.videoKey,
        is_delete: isDelete
      }
      await this.updateJobQuestionVideo(params)
    },
    handelPlayVideo () {
      this.$refs.video.play()
    },
    handleDelete (file, index) {
      this.showPreview = false
      this.updateVideoKey(0)
      this.fileList.splice(index, 1)
    },
    validate () {
      if (!this.field.required && this.fileList.length <= 0) {
        return true
      }
      if (this.fileList.length <= 0 || this.uploadLoading) {
        this.$nextTick(() => {
          const el = this.$el.querySelector('.pending-text')
          if (el) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' })
          }
        })
        return false
      } else {
        return true
      }
    }
  },
  watch: {
    data: {
      immediate: true,
      handler (value) {
        if (!_.isEmpty(value)) {
          const videoKey = this.data[this.field.name].videoKey
          if (videoKey) {
            this.fileList.splice(0, 1, {
              file: {
                name: videoKey
              }
            })
          }
          this.videoKey = videoKey
          this.uploadLoading = false
        }
      }
    }

  }
}
</script>

<style lang="scss" scoped>
.uploader-container {
  ::v-deep {
    .van-cell {
      font-size: 14px;
    }
  }

  .pending-container {
    color: #EE7815;
    height: 20px;
    margin-top: 8px;
    margin-bottom: 10px;

    .pending-icon {
      font-size: 12px;
      vertical-align: middle;
    }

    .pending-text {
      display: inline-block;
      font-size: 12px;
      margin-left: 10px;
      line-height: 16px;
    }
  }

  .error-container {
    display: flex;
    color: red;
    align-items: center;

    .error-icon {
      font-size: 14px;
      margin-right: 5px;
      line-height: 20px;
    }

    .error-message {
      display: inline-block;
      padding: 0;

      ::v-deep {
        .van-cell__value {
          color: red;
        }
      }
    }
  }

  .warn-container {
    display: flex;
    color: #EE7815;

    .warn-icon {
      font-size: 14px;
      top: 6px;
      margin-right: 5px;
    }

    .warn-message {
      padding: 0;

      ::v-deep {
        .van-cell__value {
          color: #EE7815;
        }
      }
    }
  }

  .video-preview {
    width: 100%;
    position: relative;
    display: flex;
    align-items: center;

    .video {
      width: 100%;
      max-height: 50vh;
      max-height: 50dvh;
    }

    .play-icon {
      position: absolute;
      cursor: pointer;
      top: 50%;
      left: 50%;
      width: 60px;
      height: 60px;
      margin-top: -30px;
      margin-left: -30px;
      border-radius: 50%;
      line-height: 60px;
      font-size: 30px;
      background-color: rgba(0, 0, 0, .5);
      color: #fff;
      padding-left: 18px;
      box-sizing: border-box;
    }
  }

  .preview-cover {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    .file-info {
      flex: 1;
      line-height: 1;

      .preview-icon {
        font-size: 12px;
        vertical-align: middle;
      }

      .delete-icon {
        font-size: 16px;
        vertical-align: middle;
      }

      .preview-name {
        display: inline-block;
        max-width: 250px;
        font-size: 12px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        line-height: 16px;
        margin: 0 6px;
        vertical-align: middle;
      }
    }

    .upload-loading {
      top: -2px;
      height: 12px;
      width: 12px;
      color: var(--custom-color, #0096cc);
    }

    .upload-status {
      font-size: 12px;
    }

    .success {
      color: #51BF82;
    }

    .error {
      color: red;
    }
  }

  .upload-button {
    width: max-content;
    height: 32px;
    padding: 0 15px;
    font-size: 14px;
    color: #fff;
    border-radius: 2px;
    border-width: 0;
    background-color: var(--custom-color, #0096cc);
  }

  .field {
    margin-top: 8px;
    padding: 0 5px;
    border: 1px solid #DCDFE6;

    ::v-deep {

      .van-field__body {
        min-height: 36px;
      }
    }
  }

  ::v-deep {

    .van-uploader {
      width: 100%;
    }

    .van-image-preview__cover {
      width: 100vw;
      height: 100vh;
      height: 100dvh;
      display: flex;
    }
    .van-image-preview__index {
      display: none;
    }

    .van-uploader__wrapper {
      display: flex;
      flex-wrap: nowrap;
      flex-direction: column-reverse;

      .van-uploader__preview {

        .van-uploader__preview-image {
          display: inline;

          img {
            display: none;
          }
        }
      }
    }
    .van-uploader__wrapper--disabled {
      opacity: 1;
    }

    .van-uploader__input-wrapper {
      width: max-content;
      margin-bottom: 12px;
    }

    .van-uploader__preview-cover {
      position: relative;
    }

    .van-uploader__file {
      display: inline;
      background-color: transparent;
    }

    // .van-uploader__preview-delete {
    //   display: none;
    // }

    .van-uploader__file-icon {
      display: none;
    }

    .van-uploader__file-name {
      display: none;
    }
  }

  .upload-cell {
    padding: 0;
    font-size: 12px;
    line-height: 16px;
    color: #909399;
    text-align: left;
    font-style: normal;
    white-space: break-spaces;
    word-break: break-word;

    &::after {
      border: 0;
    }
  }
}
</style>
